/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {ActionInstance} from "../../../../models/action-instance";
import {Color} from "../../../../models/color";
import {Dialog} from "../../../../models/dialog";
import {Type, DialogButton} from "../../../../models/dialog-button";
import {Group} from "../../../../models/group";
import {OnInit, Component, Input, EventEmitter, Output} from "@angular/core";
import {ActivatedRoute} from "@angular/router";

@Component({
  selector: 'app-dialog-buttons',
  templateUrl: './dialog-buttons.component.html'
})
export class DialogButtonsComponent implements OnInit {

  mButtonColors = ButtonColorUi;
  mButtonTypes = ButtonTypeUi;

  @Input()
  set dialog(dialog: Dialog) {
    this.mDialog = dialog;
    this.reset();
  }

  @Input()
  set selectedButton(button: DialogButton) {
    console.log("setSelectedButton() - Button: " + (button == null ? "*NULL*" : (button.id + "/" + button.label)));

    this.mSelectedButton = button;
    if (this.mSelectedButton == null) {
      this.reset();
    } else {
      //Set the Button Name to the selected button's name so it can be updated...
      this.mButtonName = this.mSelectedButton.label;

      //Set the Button Type and whether it is a Wait button
      switch (button.type) {
        case Type.ACCEPT: {
          this.mButtonTypeUi = ButtonTypeUi.OWNER;
          this.mIsWait = false;
          break;
        }
        case Type.ACCEPT_WAIT: {
          this.mButtonTypeUi = ButtonTypeUi.OWNER;
          this.mIsWait = true;
          break;
        }
        case Type.CLOSE: {
          this.mButtonTypeUi = ButtonTypeUi.CLOSE;
          this.mIsWait = false;
          break;
        }
        case Type.DECLINE: {
          this.mButtonTypeUi = ButtonTypeUi.DECLINE;
          this.mIsWait = false;
          break;
        }
        case Type.DECLINE_WAIT: {
          this.mButtonTypeUi = ButtonTypeUi.DECLINE;
          this.mIsWait = true;
          break;
        }
        case Type.NONE: {
          this.mButtonTypeUi = ButtonTypeUi.NORMAL;
          this.mIsWait = false;
          break;
        }
        case Type.NONE_WAIT: {
          this.mButtonTypeUi = ButtonTypeUi.NORMAL;
          this.mIsWait = true;
          break;
        }
      }

      //Set the Button Color Ui
      switch (button.color.red) {
        case 2: {
          this.mButtonColorUi = ButtonColorUi.BLACK;
          break;
        }
        case 3: {
          this.mButtonColorUi = ButtonColorUi.ORANGE;
          break;
        }
        case 4: {
          this.mButtonColorUi = ButtonColorUi.GREEN;
          break;
        }
        case 5: {
          this.mButtonColorUi = ButtonColorUi.BLUE;
          break;
        }
        case 6: {
          this.mButtonColorUi = ButtonColorUi.YELLOW;
          break;
        }
        case 7: {
          this.mButtonColorUi = ButtonColorUi.RED;
          break;
        }
        case 1:
        default: {
          this.mButtonColorUi = ButtonColorUi.WHITE;
          break;
        }
      }
    }
  }

  @Output()
  selectButton = new EventEmitter<DialogButton>();

  //Dialog which is being edited/displayed...
  mDialog: Dialog;

  //The Dialog Button which was selected by the User
  mSelectedButton: DialogButton = null;

  //Name of the Button. Shown in the UI
  mButtonName: string;

  //Color of the Button. Shown in the UI
  mButtonColorUi = ButtonColorUi.GREEN;

  //Type of the Button. Shown in the UI
  mButtonTypeUi = ButtonTypeUi.NORMAL;

  //Boolean holding whether the Button is a WAIT button or not
  mIsWait = false;


  constructor(

  ) {}


  ngOnInit(): void {
    this.reset();
  }

  reset(): void {
    this.mButtonName = null;
    this.mButtonColorUi = ButtonColorUi.GREEN;
    this.mButtonTypeUi = ButtonTypeUi.NORMAL;
    this.mIsWait = false;
  }

  addButtonClicked(): void {
    if (this.mSelectedButton == null) {
      const newButton = new DialogButton();
      newButton.label = this.mButtonName == null ? "" : this.mButtonName;
      newButton.color = this.getButtonColor();
      newButton.type = this.getButtonType();
      newButton.code = newButton.label;

      if (this.mDialog.buttons == null) {
        this.mDialog.buttons = [newButton];
      } else {
        this.mDialog.buttons.push(newButton);
      }

      newButton.code = this.mDialog.buttons.length.toString();
      newButton.order = this.mDialog.buttons.length;

      this.reset();
    } else {
      //Update the selected button's information
      this.mSelectedButton.label = this.mButtonName == null ? "" : this.mButtonName;
      this.mSelectedButton.color = this.getButtonColor();
      this.mSelectedButton.type = this.getButtonType();
      
      //Emit a "selectButton" event, informing the parent to unselect any buttons
      this.selectButton.emit(null);
    }
  }

  private getButtonColor(): Color {
    const color = new Color();
    color.red = this.getButtonColorRedValue();
    color.green = 0;
    color.blue = 0;
    return color;
  }

  private getButtonColorRedValue(): number {
    switch (this.mButtonColorUi) {
      case ButtonColorUi.BLACK: {
        return 2;
      }
      case ButtonColorUi.ORANGE: {
        return 3;
      }
      case ButtonColorUi.GREEN: {
        return 4;
      }
      case ButtonColorUi.BLUE: {
        return 5;
      }
      case ButtonColorUi.YELLOW: {
        return 6;
      }
      case ButtonColorUi.RED: {
        return 7;
      }
      case ButtonColorUi.WHITE:
      default: {
        return 1;
      }

    }
  }

  private getButtonType(): Type {
    switch (this.mButtonTypeUi) {
      case ButtonTypeUi.OWNER: {
        return this.mIsWait ? Type.ACCEPT_WAIT : Type.ACCEPT;
      }
      case ButtonTypeUi.CLOSE: {
        return Type.CLOSE;
      }
      case ButtonTypeUi.DECLINE: {
        return this.mIsWait ? Type.DECLINE_WAIT : Type.DECLINE;
      }
      case ButtonTypeUi.NORMAL:
      default: {
        return this.mIsWait ? Type.NONE_WAIT : Type.NONE;
      }
    }
  }

}

enum ButtonTypeUi {
  NORMAL = 'NORMAL BUTTON',
  OWNER = 'MAKE OWNER',
  CLOSE = 'CLOSE',
  DECLINE = 'DECLINE'
}

enum ButtonColorUi {
  WHITE = 'WHITE',
  BLACK = 'BLACK',
  ORANGE = 'ORANGE',
  GREEN = 'GREEN',
  BLUE = 'BLUE',
  YELLOW = 'YELLOW',
  RED = 'RED'
}
